import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    MenuItem,
    FormControl,
    Stack,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    SelectChangeEvent,
    TextField,
    Checkbox,
    Switch,
    FormControlLabel,
    IconButton,
    ButtonGroup
} from '@mui/material';
import { YesNoToggle } from '../../components/YesNoToggle';
import {
    ArrowBack,
    Assessment,
    CheckCircle,
    ErrorOutline,
    CheckCircleOutline,
    WarningAmber,
    BarChart,
    Error as ErrorIcon,
    Shield,
    PriorityHigh
} from '@mui/icons-material';
import TitleBar from '../../components/TitleBar/TitleBar';
import {
    getReviewQuestionsByResponseId,
    submitReviewAnswer,
    getReviewQuestionAnswers,
    getRaidItems,
    getRaidItemsAllQuestionnaires,
    getRaidItemById,
    getQuestionnaireResponseById,
    createRaidItem,
    updateRaidItem,
    getSystemEnvironment,
    type RaidItem,
    type SeparatedRaidItems,
    type QuestionnaireResponse,
    type ReviewQuestion,
    type ReviewQuestionAnswer
} from '../../services/api';
import { getUser } from '../../services/auth';
import { UserUtils } from '../../types';
import RaidDialog from './RaidDialog';
import { useNavigate, useParams } from 'react-router-dom';
import ReviewSummaryDialog from './ReviewSummaryDialog';
import SubmitReviewDialog from './SubmitReviewDialog';
import ComputedMetricsDialog from './ComputedMetricsDialog';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

// Helper function to normalize YES/NO values for consistent display
const normalizeYesNoValue = (value: string | null | undefined): string => {
    if (!value) return '';
    const normalized = value.trim().toUpperCase();
    if (normalized === 'YES' || normalized === 'NO') {
        return normalized;
    }
    if (normalized === 'Y' || normalized === 'TRUE' || normalized === '1') return 'YES';
    if (normalized === 'N' || normalized === 'FALSE' || normalized === '0') return 'NO';
    return value;
};

// Helper function to safely extract error message
const getErrorMessage = (error: unknown): string => {
    // Check if it's a standard Error object
    if (error && typeof error === 'object' && 'message' in error) {
        return (error as { message: string }).message;
    }
    // Check if it's a string
    if (typeof error === 'string') {
        return error;
    }
    // Check if it has a toString method
    if (error && typeof error === 'object' && 'toString' in error) {
        try {
            return error.toString();
        } catch {
            // Fall through to default
        }
    }
    return 'An error occurred';
};

const ReviewAnswers: React.FC = () => {
    const { responseId } = useParams<{ responseId: string }>();
    const [value, setValue] = useState(0);
    const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
    const [answers, setAnswers] = useState<ReviewQuestionAnswer[]>([]);
    const [dimensions, setDimensions] = useState<string[]>([]);
    const [raidItems, setRaidItems] = useState<{ [key: number]: SeparatedRaidItems }>({});
    const [loading, setLoading] = useState(true);
    const [loadingRaidItems, setLoadingRaidItems] = useState<Set<number>>(new Set());
    const [loadingRaidItemsBackground, setLoadingRaidItemsBackground] = useState(false);
    const [error, setError] = useState<string>('');
    const [systemEnvironment, setSystemEnvironment] = useState<string>('');  // Store system environment
    const [raidDialogOpen, setRaidDialogOpen] = useState(false);
    const [selectedQuestionAnswerId, setSelectedQuestionAnswerId] = useState<number | null>(null);
    const [reviewComments, setReviewComments] = useState<string>('');  // Store existing review comments
    const [localTextValues, setLocalTextValues] = useState<{ [key: number]: string }>({});
    const [workingRaidItems, setWorkingRaidItems] = useState<Set<number>>(new Set());
    const [reviewName, setReviewName] = useState('');
    const [reviewPeriodDisplay, setReviewPeriodDisplay] = useState('');
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [reviewStatus, setReviewStatus] = useState('');
    const [reviewEntityId, setReviewEntityId] = useState<number | null>(null);
    const navigate = useNavigate();
    const user = getUser();
    const canPerformReview = UserUtils.canPerformReview(user);
    const canSubmitReview = UserUtils.canSubmitReview(user);

    useEffect(() => {
        const fetchData = async () => {
            if (!responseId) {
                setError('Response ID not found');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');
                
                // Fetch the system environment
                const envResponse = await getSystemEnvironment();
                const env = envResponse.data.system_env;
                setSystemEnvironment(env);
                console.log(`System environment: ${env}`);

                // Fetch the specific questionnaire response by ID
                console.log('Fetching questionnaire response for ID:', responseId);
                const currentResponse = await getQuestionnaireResponseById(Number(responseId));
                const currentResponseData = currentResponse.data;

                if (!currentResponseData) {
                    setError('Response not found');
                    setLoading(false);
                    return;
                }

                setReviewName(currentResponseData.review_name);
                setReviewPeriodDisplay(currentResponseData.review_period_display || '');
                setReviewStatus(currentResponseData.review_status);
                setReviewEntityId(currentResponseData.review_entity_id || null);
                
                // Store review comments if they exist
                if (currentResponseData.review_comments) {
                    setReviewComments(currentResponseData.review_comments);
                }

                // Extract review_definition_id and review_entity_type_id
                const reviewDefinitionId = currentResponseData.review_definition?.id || currentResponseData.review_definition_id;
                
                // Handle review_entity_type_name which can be either a string or an object
                let reviewEntityTypeId = currentResponseData.review_entity_type_id;
                if (!reviewEntityTypeId && typeof currentResponseData.review_entity_type_name === 'object') {
                    reviewEntityTypeId = currentResponseData.review_entity_type_name.id;
                }

                console.log('Review Definition ID:', reviewDefinitionId);
                console.log('Review Entity Type ID:', reviewEntityTypeId);

                // Validate required IDs are present
                if (!reviewDefinitionId || !reviewEntityTypeId) {
                    setError('Missing review definition or entity type information');
                    setLoading(false);
                    return;
                }

                // Step 1: First, try to fetch existing answers
                console.log('Fetching existing answers for response ID:', responseId);
                const answersRes = await getReviewQuestionAnswers(Number(responseId));
                setAnswers(answersRes.data);

                let questionsRes;
                // Step 2: If no answers exist, this is the first time - fetch questions
                if (!answersRes.data || answersRes.data.length === 0) {
                    console.log('No existing answers found. Fetching questions for first-time review.');
                    questionsRes = await getReviewQuestionsByResponseId(
                        reviewDefinitionId,
                        reviewEntityTypeId
                    );
                    setQuestions(questionsRes.data);
                } else {
                    // If answers exist, we still need questions to display them properly
                    console.log('Existing answers found. Fetching questions to display answers.');
                    questionsRes = await getReviewQuestionsByResponseId(
                        reviewDefinitionId,
                        reviewEntityTypeId
                    );
                    setQuestions(questionsRes.data);
                }
                
                // Auto-populate answers for new questions based on system environment
                const existingAnswerQuestionIds = new Set(answersRes.data.map((a: ReviewQuestionAnswer) => a.review_question_id));
                const unansweredQuestions = questionsRes.data.filter((q: ReviewQuestion) => !existingAnswerQuestionIds.has(q.id));
                
                if (unansweredQuestions.length > 0 && user?.id) {
                    console.log(`Pre-populating ${unansweredQuestions.length} unanswered questions`);
                    console.log(`Env: ${env}`);
                    
                    // Check environment to decide which value to use for pre-population
                    const isLocalOrDemo = env === 'local' || env === 'demo';
                    console.log(`Using ${isLocalOrDemo ? 'ideal_value' : 'default_value'} for pre-population`);
                    
                    // Create pre-populated answers
                    const prePopulatedAnswers = unansweredQuestions.map((question: ReviewQuestion) => {
                        // Use type assertion since ReviewQuestion type may not include these properties
                        const q = question as ReviewQuestion & { ideal_value?: string, default_value?: string };
                        const valueToUse = isLocalOrDemo && q.ideal_value ? 
                            q.ideal_value : q.default_value;
                            
                        // Only pre-populate if we have a value to use
                        if (valueToUse) {
                            // For YES-NO values, normalize before submitting
                            let answerValues: string[];
                            if (question.answer_type === 'YES-NO') {
                                answerValues = [normalizeYesNoValue(valueToUse)];
                            } else if (
                                (question.answer_type === 'SELECT' || question.answer_type === 'MULTI-SELECT') &&
                                isLocalOrDemo && q.ideal_value
                            ) {
                                // ideal_value may be comma-separated; SELECT uses first, MULTI-SELECT uses all
                                const ideals = q.ideal_value.split(',').map(v => v.trim()).filter(Boolean);
                                answerValues = question.answer_type === 'SELECT'
                                    ? ideals.slice(0, 1)
                                    : ideals;
                            } else {
                                answerValues = [valueToUse];
                            }
                                
                            return submitReviewAnswer({
                                review_question_id: question.id,
                                review_questionnaire_response_id: Number(responseId),
                                answer_value: answerValues,
                                submitted_by: user.id
                            });
                        }
                        return Promise.resolve(null);
                    });
                    
                    // Wait for all pre-population requests to complete
                    const results = await Promise.all(prePopulatedAnswers);
                    const validResults = results.filter(Boolean);
                    
                    if (validResults.length > 0) {
                        // Refresh the answers list to get the newly pre-populated answers
                        const refreshedAnswers = await getReviewQuestionAnswers(Number(responseId));
                        setAnswers(refreshedAnswers.data);
                    }
                }

                // Extract unique dimensions
                const dimensionSet = new Set<string>();
                questionsRes.data.forEach((question: ReviewQuestion) => {
                    if (question.review_dimension) {
                        // Handle review_dimension as either string or object
                        const dimensionName = typeof question.review_dimension === 'string' 
                            ? question.review_dimension 
                            : question.review_dimension.name;
                        dimensionSet.add(dimensionName);
                    }
                });
                const uniqueDimensions = Array.from(dimensionSet).sort();
                setDimensions(uniqueDimensions);

                // Set loading to false here to show the main content immediately
                setLoading(false);

                // Load RAID items in the background after main content is displayed
                loadRaidItemsInBackground(answersRes.data);
            } catch (err: unknown) {
                setError(getErrorMessage(err));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [responseId]);

    // Background function to load RAID items asynchronously
    const loadRaidItemsInBackground = async (answers: ReviewQuestionAnswer[]) => {
        try {
            setLoadingRaidItemsBackground(true);
            console.log('Loading RAID items in background...');
            
            const currentQuestionnaireResponseId = Number(responseId);
            
            // Load RAID items for all answers in parallel using the new all_questionnaires API
            const raidItemsPromises = answers.map(async (answer: ReviewQuestionAnswer) => {
                try {
                    const raidResponse = await getRaidItemsAllQuestionnaires(answer.id);
                    const allItems: RaidItem[] = raidResponse.data || [];
                    
                    // Separate current questionnaire items from others
                    const current = allItems.filter(item => 
                        item.questionnaire_response_id === currentQuestionnaireResponseId
                    );
                    const others = allItems.filter(item => 
                        item.questionnaire_response_id !== currentQuestionnaireResponseId
                    );
                    
                    return { 
                        answerId: answer.id, 
                        items: { current, others } as SeparatedRaidItems 
                    };
                } catch (error: unknown) {
                    console.error(`Error loading RAID items for answer ${answer.id}:`, getErrorMessage(error));
                    return { 
                        answerId: answer.id, 
                        items: { current: [], others: [] } as SeparatedRaidItems 
                    };
                }
            });

            const raidResults = await Promise.all(raidItemsPromises);
            const raidItemsMap = raidResults.reduce((acc, { answerId, items }) => {
                acc[answerId] = items;
                return acc;
            }, {} as { [key: number]: SeparatedRaidItems });

            setRaidItems(raidItemsMap);
            console.log('RAID items loaded in background successfully');
        } catch (error: unknown) {
            console.error('Error loading RAID items in background:', getErrorMessage(error));
        } finally {
            setLoadingRaidItemsBackground(false);
        }
    };

    const getQuestionReviewDimension = (questionAnswerId: number | null): string | undefined => {
        if (!questionAnswerId) return undefined;
        const answer = answers.find(a => a.id === questionAnswerId);
        if (!answer) return undefined;
        const question = questions.find(q => q.id === answer.review_question_id);
        if (!question?.review_dimension) return undefined;
        return typeof question.review_dimension === 'string'
            ? question.review_dimension
            : question.review_dimension.name;
    };

    const handleRaidClick = async (questionAnswerId: number) => {
        setSelectedQuestionAnswerId(questionAnswerId);
        setRaidDialogOpen(true);

        // Load RAID items if not already loaded by background fetch
        // This should rarely happen since loadRaidItemsInBackground loads all items
        if (!raidItems[questionAnswerId]) {
            try {
                setLoadingRaidItems(prev => new Set(prev).add(questionAnswerId));
                const currentQuestionnaireResponseId = Number(responseId);
                const response = await getRaidItemsAllQuestionnaires(questionAnswerId);
                const allItems: RaidItem[] = response.data || [];
                
                // Separate current questionnaire items from others
                const current = allItems.filter(item => 
                    item.questionnaire_response_id === currentQuestionnaireResponseId
                );
                const others = allItems.filter(item => 
                    item.questionnaire_response_id !== currentQuestionnaireResponseId
                );
                
                setRaidItems(prev => ({
                    ...prev,
                    [questionAnswerId]: { current, others }
                }));
            } catch (error) {
                console.error('Error loading RAID items:', error);
            } finally {
                setLoadingRaidItems(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(questionAnswerId);
                    return newSet;
                });
            }
        }
    };

    const handleRaidSubmit = async () => {
        if (selectedQuestionAnswerId) {
            try {
                const currentQuestionnaireResponseId = Number(responseId);
                const response = await getRaidItemsAllQuestionnaires(selectedQuestionAnswerId);
                const allItems: RaidItem[] = response.data || [];
                
                // Separate current questionnaire items from others
                const current = allItems.filter(item => 
                    item.questionnaire_response_id === currentQuestionnaireResponseId
                );
                const others = allItems.filter(item => 
                    item.questionnaire_response_id !== currentQuestionnaireResponseId
                );
                
                setRaidItems(prev => ({
                    ...prev,
                    [selectedQuestionAnswerId]: { current, others }
                }));
            } catch (error) {
                console.error('Error reloading RAID items:', error);
            }
        }
    };

    const handleOverdueRaidDetected = async (raidId: number) => {
        try {
            const response = await getRaidItemById(raidId);
            const raidItem = response.data;
            const entityType = raidItem?.entity_type;
            const entityId = raidItem?.entity_id;

            // Auto-open question-level RAID editor to fix overdue dates quickly.
            if (entityType === 'review_question_answer' && entityId) {
                setSubmitDialogOpen(false);
                await handleRaidClick(Number(entityId));
            }
        } catch (error) {
            console.error('Error navigating to overdue RAID item:', error);
        }
    };

    const handleAnswerChange = async (questionId: number, value: string[]) => {
        if (!responseId || !user?.id) return;

        try {
            const response = await submitReviewAnswer({
                review_question_id: questionId,
                review_questionnaire_response_id: Number(responseId),
                answer_value: value,
                submitted_by: user.id
            });

            // Update local state
            setAnswers(prev => {
                const existingAnswerIndex = prev.findIndex(a => a.review_question_id === questionId);
                if (existingAnswerIndex >= 0) {
                    // Update existing answer
                    return prev.map((answer, index) =>
                        index === existingAnswerIndex
                            ? { ...answer, answer_value: value }
                            : answer
                    );
                } else {
                    // Add new answer with all required fields
                    return [...prev, {
                        id: response.data.id,
                        review_question_id: questionId,
                        review_questionnaire_response_id: Number(responseId),
                        answer_value: value,
                        submitted_by: user.id,
                        submitted_by_name: {
                            id: user.id,
                            user_name: user.user_name,
                            roles: user.roles.map(role => role.role_name)
                        },
                        submitted_at: new Date().toISOString()
                    }];
                }
            });
        } catch (error) {
            console.error('Error submitting answer:', error);
        }
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const handleSelectChange = (questionId: number) => (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value as string[];
        handleAnswerChange(questionId, value);
    };

    const handleCheckboxChange = (questionId: number, option: string, currentValue: string[]) => {
        const newValue = currentValue.includes(option)
            ? currentValue.filter(v => v !== option)
            : [...currentValue, option];
        handleAnswerChange(questionId, newValue);
    };

    const handleToggleChange = (questionId: number, isYes: boolean) => {
        const newValue = isYes ? ['YES'] : ['NO'];
        handleAnswerChange(questionId, newValue);
    };

    const getRaidItemsList = (questionAnswerId: number): SeparatedRaidItems => {
        return raidItems[questionAnswerId] || { current: [], others: [] };
    };

    // Helper function to parse ideal value for NUMERIC/PERCENTAGE types
    const parseIdealValue = (idealValue: string): { type: 'comparison' | 'range' | 'equality'; operator?: string; value?: number; min?: number; max?: number } | null => {
        if (!idealValue) return null;
        
        const valueStr = idealValue.toString().trim();
        
        // Check for range format (e.g., "20-80", "30%-50%")
        const rangePattern = /^(\d+\.?\d*)\s*%?\s*-\s*(\d+\.?\d*)\s*%?$/;
        const rangeMatch = valueStr.match(rangePattern);
        if (rangeMatch) {
            return { type: 'range', min: parseFloat(rangeMatch[1]), max: parseFloat(rangeMatch[2]) };
        }
        
        // Check for comparison format (e.g., ">80", "<50", ">=80", "<=50", "=100")
        const comparisonPattern = /^([><=]=?|[><=])\s*(\d+\.?\d*)\s*%?$/;
        const comparisonMatch = valueStr.match(comparisonPattern);
        if (comparisonMatch) {
            return { type: 'comparison', operator: comparisonMatch[1], value: parseFloat(comparisonMatch[2]) };
        }
        
        // Check for plain number (equality)
        const plainNumberPattern = /^(\d+\.?\d*)\s*%?$/;
        const plainNumberMatch = valueStr.match(plainNumberPattern);
        if (plainNumberMatch) {
            return { type: 'equality', value: parseFloat(plainNumberMatch[1]) };
        }
        
        return null;
    };
    
    // Check if a numeric/percentage value is within the ideal range
    const isValueWithinIdeal = (actualValue: string, idealValue: string): boolean => {
        const parsed = parseIdealValue(idealValue);
        if (!parsed) return false;
        
        const actual = parseFloat(actualValue);
        if (isNaN(actual)) return false;
        
        switch (parsed.type) {
            case 'range':
                return actual >= (parsed.min || 0) && actual <= (parsed.max || 0);
            case 'comparison':
                switch (parsed.operator) {
                    case '>': return actual > (parsed.value || 0);
                    case '<': return actual < (parsed.value || 0);
                    case '>=': return actual >= (parsed.value || 0);
                    case '<=': return actual <= (parsed.value || 0);
                    case '=': return actual === parsed.value;
                    default: return false;
                }
            case 'equality':
                return actual === parsed.value;
            default:
                return false;
        }
    };
    
    // Calculate suggested value based on ideal_value specification
    // For ">80", suggests 81; for "100", suggests 100; for "20-80", suggests 20
    const getSuggestedValue = (idealValue: string | undefined, answerType?: string): string | null => {
        if (!idealValue) return null;
        
        const parsed = parseIdealValue(idealValue);
        if (!parsed) return null;
        
        const isPercentage = answerType === 'PERCENTAGE';
        const maxValue = isPercentage ? 100 : null;
        
        switch (parsed.type) {
            case 'range':
                // For range, suggest the minimum value
                return parsed.min?.toString() || null;
            case 'comparison':
                let suggested: number;
                switch (parsed.operator) {
                    case '>':
                        suggested = (parsed.value || 0) + 1;
                        if (maxValue && suggested > maxValue) suggested = maxValue;
                        break;
                    case '<':
                        suggested = (parsed.value || 0) - 1;
                        if (suggested < 0) suggested = 0;
                        break;
                    case '>=':
                    case '<=':
                    case '=':
                        suggested = parsed.value || 0;
                        break;
                    default:
                        return null;
                }
                return suggested % 1 === 0 ? suggested.toString() : suggested.toFixed(2);
            case 'equality':
                return parsed.value?.toString() || null;
            default:
                return null;
        }
    };

    // Helper function to check if answer is non-ideal (based on backend logic)
    const isNonIdealAnswer = (answerValue: any[], idealValue: string | undefined, answerType?: string): boolean => {
        if (!answerValue || answerValue.length === 0) {
            return false;
        }
        if (!idealValue) {
            return false;
        }
        
        const actualValue = answerValue[0]?.toString();
        
        // For NUMERIC and PERCENTAGE types, use the parsing logic
        if (answerType === 'NUMERIC' || answerType === 'PERCENTAGE') {
            return !isValueWithinIdeal(actualValue, idealValue);
        }
        
        // For SELECT and MULTI-SELECT, ideal_value can contain multiple
        // comma-separated values. Answer is ideal when it matches any of them
        // (lenient: also ideal when it includes all configured ideals).
        // Comparison trims whitespace and is case-insensitive.
        if (answerType === 'SELECT' || answerType === 'MULTI-SELECT') {
            const normalize = (v: any) => v?.toString().trim().toLowerCase();
            const idealSet = idealValue.split(',').map(normalize).filter(Boolean);
            const answerSet = answerValue.map(normalize).filter(Boolean);

            if (idealSet.length === 0) {
                return false;
            }

            const includesAll = idealSet.every(ideal => answerSet.includes(ideal));
            const includesAny = idealSet.some(ideal => answerSet.includes(ideal));
            return !(includesAll || includesAny);
        }
        
        // For other types, use simple case-insensitive comparison
        return actualValue?.toLowerCase() !== idealValue.toLowerCase();
    };

    // Three-status system: 'green' | 'amber' | 'red' | 'unanswered'
    const getAnswerStatus = (questionId: number): 'green' | 'amber' | 'red' | 'unanswered' => {
        const question = questions.find(q => q.id === questionId);
        const answer = answers.find(a => a.review_question_id === questionId);
        
        // If no answer or empty answer, return unanswered
        if (!answer?.answer_value || answer.answer_value.length === 0) {
            return 'unanswered';
        }
        
        // If answer matches ideal value (case-insensitive), return green
        if (!isNonIdealAnswer(answer.answer_value, question?.ideal_value, question?.answer_type)) {
            return 'green';
        }
        
        // Answer is non-ideal, check if there are RAID items
        const raidItemsList = getRaidItemsList(answer.id);
        const hasRaidItems = raidItemsList.current.length > 0 || raidItemsList.others.length > 0;
        
        // If non-ideal but has RAID items, return amber
        if (hasRaidItems) {
            return 'amber';
        }
        
        // If non-ideal and no RAID items, return red
        return 'red';
    };

    const getDimensionStatus = (dimension: string): 'complete' | 'incomplete' => {
        const dimensionQuestions = questions.filter(q => {
            const dimensionName = typeof q.review_dimension === 'string' 
                ? q.review_dimension 
                : q.review_dimension?.name;
            return dimensionName === dimension;
        });
        const allAnswered = dimensionQuestions.every(q => getAnswerStatus(q.id) !== 'unanswered');
        return allAnswered ? 'complete' : 'incomplete';
    };

    // Function to render status icon based on answer status
    const renderStatusIcon = (questionId: number) => {
        const status = getAnswerStatus(questionId);
        
        switch (status) {
            case 'green':
                return (
                    <Tooltip title="All clear">
                        <CheckCircleOutline color="success" />
                    </Tooltip>
                );
            case 'amber':
                return (
                    <Tooltip title="Attention Required. RAID items logged">
                        <Shield sx={{ color: 'orange' }} />
                    </Tooltip>
                );
            case 'red':
                return (
                    <Tooltip title="Needs attention. Log/update RAID items">
                        <PriorityHigh color="error" />
                    </Tooltip>
                );
            case 'unanswered':
            default:
                return (
                    <Tooltip title="Unanswered question">
                        <WarningAmber color="warning" />
                    </Tooltip>
                );
        }
    };

    const renderTabLabel = (dimension: string) => {
        const status = getDimensionStatus(dimension);
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {dimension}
                {status === 'complete' ? (
                    <CheckCircleOutline sx={{ fontSize: 16 }} color="success" />
                ) : (
                    <WarningAmber sx={{ fontSize: 16 }} color="warning" />
                )}
            </Box>
        );
    };
    
    const getRaidIconColor = (questionId: number) => {
        const answer = answers.find(a => a.review_question_id === questionId);
        if (!answer) return "default";
        if (workingRaidItems.has(answer.id)) return "warning";
        if (loadingRaidItems.has(answer.id)) return "default";
        const raidItemsList = raidItems[answer.id];
        if (raidItemsList && (raidItemsList.current.length > 0 || raidItemsList.others.length > 0)) return "error";
        return "default";
    };
    
    // Render different answer input types based on question type.
    // Keep submitted questionnaires editable for reviewers.
    const renderAnswerInput = (question: ReviewQuestion, answerValue: string[]) => {
        // Function to handle local text changes before submission
        const handleLocalTextChange = (questionId: number, value: string) => {
            setLocalTextValues(prev => ({
                ...prev,
                [questionId]: value
            }));
        };
        
        // After submit, only performers (REVIEWER) may continue editing; DES stays read-only.
        const isReadOnly = reviewStatus === 'SUBMITTED' && !canPerformReview;
        
        // Get locally stored text value or use the actual answer value
        const getTextValue = (questionId: number, defaultValue: string) => {
            return localTextValues[questionId] !== undefined
                ? localTextValues[questionId]
                : defaultValue;
        };
        
        switch (question.answer_type) {
            case 'YES-NO':
                // For YES-NO, render toggle switch
                return (
                    <YesNoToggle
                        value={answerValue}
                        onChange={(isYes) => !isReadOnly && handleToggleChange(question.id, isYes)}
                        disabled={isReadOnly}
                        size="medium"
                        showLabels={true}
                    />
                );
            
            case 'SELECT':
            case 'MULTI-SELECT':
                // Due to protobuf synchronization issues between backend/frontend definitions
                // we need to use type assertions to handle potential type mismatches
                
                // Cast the entire question to any first to bypass TypeScript's strict checking
                const anyQuestion = question as any;
                let options: string[] = [];
                
                try {
                    // Check if answer_options exists with any type
                    const answerOptions = anyQuestion.answer_options;
                    
                    if (Array.isArray(answerOptions)) {
                        // If it's already an array, use it directly
                        options = answerOptions;
                    } else if (typeof answerOptions === 'string') {
                        // If it's a string, split it by comma
                        options = answerOptions.split(',').map((opt: string) => opt.trim());
                    } else if (answerOptions) {
                        // Convert to string and try to split if possible
                        try {
                            const strOptions = String(answerOptions);
                            if (strOptions) {
                                options = strOptions.split(',').map((opt: string) => opt.trim());
                            }
                        } catch (e) {
                            console.warn('Failed to parse answer options:', e);
                        }
                    }
                } catch (e) {
                    console.error('Error processing answer options:', e);
                }
                        
                const isMultiSelect = question.answer_type === 'MULTI-SELECT';

                if (!isMultiSelect) {
                    // SELECT allows a single value only. answer_value is still stored as
                    // an array to keep the data model consistent across answer types.
                    const singleValue = answerValue[0] || '';
                    return (
                        <FormControl fullWidth size="small">
                            <Select
                                value={singleValue}
                                onChange={isReadOnly ? undefined : (event) => {
                                    const value = event.target.value as string;
                                    handleAnswerChange(question.id, value ? [value] : []);
                                }}
                                displayEmpty
                                readOnly={isReadOnly}
                                disabled={isReadOnly}
                                size="small"
                                sx={{
                                    opacity: isReadOnly ? 0.8 : 1,
                                    // Preserve color coding for selected value even in read-only mode
                                    '.MuiSelect-select': {
                                        color: singleValue === 'YES' ? 'success.main' :
                                               singleValue === 'NO' ? 'error.main' : 'text.primary',
                                    }
                                }}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <em>Select an option</em>;
                                    }
                                    return selected as string;
                                }}
                            >
                                <MenuItem disabled value="">
                                    <em>Select an option</em>
                                </MenuItem>
                                {options.map((option: string) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                return (
                    <FormControl fullWidth size="small">
                        <Select
                            value={answerValue}
                            onChange={isReadOnly ? undefined : handleSelectChange(question.id)}
                            displayEmpty
                            multiple
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                            size="small"
                            sx={{
                                opacity: isReadOnly ? 0.8 : 1,
                                // Preserve color coding for selected values even in read-only mode
                                '.MuiSelect-select': {
                                    color: answerValue.includes('YES') ? 'success.main' : 
                                           answerValue.includes('NO') ? 'error.main' : 'text.primary',
                                }
                            }}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return <em>Select options</em>;
                                }
                                return selected.join(', ');
                            }}
                        >
                            <MenuItem disabled value="">
                                <em>Select options</em>
                            </MenuItem>
                            {options.map((option: string) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            
            case 'PERCENTAGE':
                // For percentage answers, render a number input with 0-100 range
                const percentageValue = getTextValue(question.id, answerValue[0] || '');
                const anyQ = question as any;
                const suggestedPercentage = getSuggestedValue(anyQ.ideal_value, 'PERCENTAGE');
                const percentagePlaceholder = suggestedPercentage ? `Ideal: ${suggestedPercentage}%` : '0-100';
                return (
                    <TextField
                        type="number"
                        size="small"
                        value={percentageValue}
                        onChange={(e) => {
                            if (isReadOnly) return;
                            // Clamp value between 0 and 100
                            let val = e.target.value;
                            if (val !== '') {
                                const numVal = parseFloat(val);
                                if (numVal < 0) val = '0';
                                if (numVal > 100) val = '100';
                            }
                            handleLocalTextChange(question.id, val);
                        }}
                        onBlur={() => {
                            if (isReadOnly) return;
                            if (localTextValues[question.id] !== undefined) {
                                handleAnswerChange(question.id, [localTextValues[question.id]]);
                            }
                        }}
                        placeholder={percentagePlaceholder}
                        InputProps={{ 
                            readOnly: isReadOnly,
                            endAdornment: <Typography sx={{ ml: 1 }}>%</Typography>,
                            inputProps: { min: 0, max: 100, step: 1 }
                        }}
                        disabled={isReadOnly}
                        sx={{
                            width: 150,
                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: '#000000',
                                opacity: 0.8,
                            },
                        }}
                    />
                );
            
            case 'NUMERIC':
                // For numeric answers, render a number input
                const numericValue = getTextValue(question.id, answerValue[0] || '');
                const anyQNumeric = question as any;
                const suggestedNumeric = getSuggestedValue(anyQNumeric.ideal_value, 'NUMERIC');
                const numericPlaceholder = suggestedNumeric ? `Ideal: ${suggestedNumeric}` : 'Enter number';
                return (
                    <TextField
                        type="number"
                        size="small"
                        value={numericValue}
                        onChange={(e) => {
                            if (isReadOnly) return;
                            handleLocalTextChange(question.id, e.target.value);
                        }}
                        onBlur={() => {
                            if (isReadOnly) return;
                            if (localTextValues[question.id] !== undefined) {
                                handleAnswerChange(question.id, [localTextValues[question.id]]);
                            }
                        }}
                        placeholder={numericPlaceholder}
                        InputProps={{ 
                            readOnly: isReadOnly,
                            inputProps: { step: 'any' }
                        }}
                        disabled={isReadOnly}
                        sx={{
                            width: 150,
                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: '#000000',
                                opacity: 0.8,
                            },
                        }}
                    />
                );
            
            case 'TEXT':
            default:
                // For text answers, render a text field
                const textValue = getTextValue(question.id, answerValue[0] || '');
                return (
                    <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        size="small"
                        value={textValue}
                        onChange={(e) => {
                            if (isReadOnly) return;
                            handleLocalTextChange(question.id, e.target.value);
                        }}
                        onBlur={() => {
                            if (isReadOnly) return;
                            if (localTextValues[question.id] !== undefined) {
                                handleAnswerChange(question.id, [localTextValues[question.id]]);
                            }
                        }}
                        placeholder="Enter your answer"
                        InputProps={{ readOnly: isReadOnly }}
                        disabled={isReadOnly}
                        // Preserve styling in read-only mode
                        sx={{
                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: '#000000',
                                opacity: 0.8,
                            },
                        }}
                    />
                );
        }
    };

    return (
        <Box>
            <TitleBar />
            <Container>
                <Box sx={{ mt: 4, mb: 3 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {reviewName}
                    </Typography>
                    {reviewPeriodDisplay && (
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                            Review Period: {reviewPeriodDisplay}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                onClick={() => setSummaryDialogOpen(true)}
                                startIcon={<Assessment />}
                                sx={{ color: 'black', borderColor: 'rgba(0, 0, 0, 0.23)' }}
                            >
                                Review Summary
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setMetricsDialogOpen(true)}
                                startIcon={<BarChart />}
                                sx={{ color: 'black', borderColor: 'rgba(0, 0, 0, 0.23)' }}
                            >
                                View Metrics
                            </Button>
                            {canPerformReview && reviewStatus === 'SUBMITTED' ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setSubmitDialogOpen(true)}
                                >
                                    Close Review
                                </Button>
                            ) : canSubmitReview && reviewStatus !== 'SUBMITTED' ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setSubmitDialogOpen(true)}
                                >
                                    Submit Review
                                </Button>
                            ) : reviewStatus === 'SUBMITTED' ? (
                                <Button
                                    variant="contained"
                                    color="success"
                                    sx={{ pointerEvents: 'none' }}
                                >
                                    Review Submitted
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    sx={{ pointerEvents: 'none' }}
                                >
                                    In Progress
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Box>

                {/* Background RAID loading indicator */}
                {loadingRaidItemsBackground && (
                    <Alert 
                        severity="info" 
                        sx={{ mb: 2 }}
                        icon={<CircularProgress size={20} />}
                    >
                        Loading RAID items in background...
                    </Alert>
                )}

                {loading ? (
                    <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                        <CircularProgress />
                    </Paper>
                ) : error ? (
                    <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                        <Alert severity="error">{error}</Alert>
                    </Paper>
                ) : questions.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                        <Typography variant="h6" color="text.secondary">
                            No questions found
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={value} onChange={handleTabChange}>
                                {dimensions.map((dimension, index) => (
                                    <Tab 
                                        key={dimension} 
                                        label={renderTabLabel(dimension)} 
                                        id={`tab-${index}`} 
                                    />
                                ))}
                            </Tabs>
                        </Box>
                        {dimensions.map((dimension, index) => (
                            <TabPanel key={dimension} value={value} index={index}>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Question</TableCell>
                                                <TableCell>Answer</TableCell>
                                                <TableCell align="center">RAID</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {questions
                                                .filter(q => {
                                                    const dimensionName = typeof q.review_dimension === 'string' 
                                                        ? q.review_dimension 
                                                        : q.review_dimension?.name;
                                                    return dimensionName === dimension;
                                                })
                                                .map(question => {
                                                    const answer = answers.find(a => a.review_question_id === question.id);
                                                    const answerValue = answer?.answer_value || [];
                                                    return (
                                                        <TableRow key={question.id}>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {renderStatusIcon(question.id)}
                                                                    <Typography variant="body1">
                                                                        {question.review_question_text}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                {renderAnswerInput(question, answerValue)}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <IconButton
                                                                    onClick={() => {
                                                                        if (answer) {
                                                                            handleRaidClick(answer.id);
                                                                        } else {
                                                                            // First create an answer if it doesn't exist
                                                                            handleAnswerChange(question.id, ['']).then(() => {
                                                                                const newAnswer = answers.find(a => a.review_question_id === question.id);
                                                                                if (newAnswer) {
                                                                                    handleRaidClick(newAnswer.id);
                                                                                }
                                                                            });
                                                                        }
                                                                    }}
                                                                    color={getRaidIconColor(question.id)}
                                                                    disabled={answer ? loadingRaidItems.has(answer.id) : false}
                                                                >
                                                                    {answer && loadingRaidItems.has(answer.id) ? (
                                                                        <CircularProgress size={20} />
                                                                    ) : (
                                                                        <Assessment />
                                                                    )}
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </TabPanel>
                        ))}
                    </>
                )}
            </Container>
            <RaidDialog
                open={raidDialogOpen}
                onClose={() => {
                    setRaidDialogOpen(false);
                    setSelectedQuestionAnswerId(null);
                }}
                questionAnswerId={selectedQuestionAnswerId || 0}
                onSubmit={handleRaidSubmit}
                raidItems={getRaidItemsList(selectedQuestionAnswerId || 0)}
                readOnly={reviewStatus === 'SUBMITTED' && !canPerformReview}
                reviewDimension={getQuestionReviewDimension(selectedQuestionAnswerId)}
                reviewEntityId={reviewEntityId}
            />
            <ReviewSummaryDialog
                open={summaryDialogOpen}
                onClose={() => setSummaryDialogOpen(false)}
                responseId={Number(responseId)}
            />
            <ComputedMetricsDialog
                open={metricsDialogOpen}
                onClose={() => setMetricsDialogOpen(false)}
                reviewEntityId={reviewEntityId}
            />
            <SubmitReviewDialog 
                open={submitDialogOpen}
                onClose={() => setSubmitDialogOpen(false)}
                responseId={Number(responseId)}
                initialComments={reviewComments}
                reviewStatus={reviewStatus}
                onOverdueRaidDetected={handleOverdueRaidDetected}
                onSubmitSuccess={() => {
                    // After successful submission, go back to previous page
                    navigate(-1);
                }}
            />
        </Box>
    );
};

export default ReviewAnswers;